import { ExclamationCircleFilled, InfoCircleFilled, WarningOutlined } from '@ant-design/icons';
import { ReactNode } from 'react';

import { subtract } from '@willow/shared-iso';
import { assertNever, paymentAmountFormat } from '@willow/shared-web';

import { PaymentType } from '../helpers';

export type PaymentConfirmStatus = 'overpayment' | 'underpayment' | 'autopayOverride' | 'exact';

export interface PaymentConfirmInput {
  paymentType: PaymentType;
  paymentAmount: number;
  amountDue: number;
  autopayEnabled: boolean;
}

export interface PaymentConfirmResult {
  status: PaymentConfirmStatus;
  overpaymentAmount: number;
}

export function getPaymentConfirmStatus({
  paymentType,
  paymentAmount,
  amountDue,
  autopayEnabled,
}: PaymentConfirmInput): PaymentConfirmResult {
  const isOtherPayment = paymentType === 'other';
  const overpaymentAmount = subtract(paymentAmount, amountDue);

  if (isOtherPayment && paymentAmount > amountDue) {
    return { status: 'overpayment', overpaymentAmount };
  }

  if (isOtherPayment && paymentAmount < amountDue && amountDue > 0) {
    return { status: 'underpayment', overpaymentAmount };
  }

  if (autopayEnabled && (paymentType === 'fullPayment' || paymentType === 'fullPaymentWithShortage')) {
    return { status: 'autopayOverride', overpaymentAmount };
  }

  return { status: 'exact', overpaymentAmount };
}

export function getPaymentConfirmTitle(status: PaymentConfirmStatus): ReactNode {
  switch (status) {
    case 'overpayment':
      return (
        <>
          <InfoCircleFilled className="u-color-blue1 me-2" />
          Overpayment Confirmation
        </>
      );
    case 'underpayment':
      return (
        <>
          <ExclamationCircleFilled className="u-color-red1 me-2" />
          Underpayment Detected
        </>
      );
    case 'autopayOverride':
      return (
        <>
          <WarningOutlined className="u-color-red1 me-2" />
          Submit Payment Immediately?
        </>
      );
    case 'exact':
      return 'Confirm Payment';
    default:
      assertNever(status);
  }
}

export const PAYMENT_CONFIRM_CANCEL_LABELS: Record<PaymentConfirmStatus, string> = {
  overpayment: 'Cancel',
  underpayment: 'Cancel',
  autopayOverride: 'No, do not submit payment',
  exact: 'Cancel',
};

export const PAYMENT_CONFIRM_SUBMIT_LABELS: Record<PaymentConfirmStatus, string> = {
  overpayment: 'Confirm Payment',
  underpayment: 'Confirm Payment',
  autopayOverride: 'Yes, submit now',
  exact: 'Confirm Payment',
};
export interface PaymentConfirmCopyInput {
  status: PaymentConfirmStatus;
  overpaymentAmount: number;
  amountDue: number;
  autopayEnabled: boolean;
}

export function getPaymentConfirmCopy({
  status,
  overpaymentAmount,
  amountDue,
  autopayEnabled,
}: PaymentConfirmCopyInput): ReactNode {
  switch (status) {
    case 'overpayment':
      return (
        <p>
          You are paying <b>{paymentAmountFormat(overpaymentAmount)}</b> more than the amount due of{' '}
          <b>{paymentAmountFormat(amountDue)}</b>. The excess will be applied to your statement balance.
        </p>
      );
    case 'underpayment':
      return (
        <>
          <p>
            Your amount due is <b>{paymentAmountFormat(amountDue)}</b>.
          </p>
          <p>A partial payment will not be counted towards your loan until made in full.</p>
          {autopayEnabled && <p>The remaining balance is scheduled for auto-payment on your payment's due date.</p>}
        </>
      );
    case 'autopayOverride':
      return (
        <p>
          You currently have auto-payment enabled, submitting payment today will override auto-payment and begin{' '}
          <span className="u-color-red1 fw-bold">processing for immediate withdraw today</span>.
        </p>
      );
    case 'exact':
      return <p>Please confirm your payment details below.</p>;
    default:
      assertNever(status);
  }
}
